import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { API_BASE, authToken } from '@argo/common/api'
import { api, day, money, type Claim } from '../api'
import { useSession } from '../session'

/** One claim: what was asked, what has been paid, and who decided what. */
export default function ClaimDetail() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const claim = useQuery({ queryKey: ['claim', id], queryFn: () => api.claim(id), refetchInterval: 15_000 })
  const { me } = useSession()
  const canDecide = !!me && (me.staff?.claims || me.roles.includes('operator'))

  if (claim.isLoading) return <div className="page page-narrow"><div className="skeleton" style={{ height: 160 }} /></div>
  if (claim.isError) return <div className="page page-narrow"><p className="empty">{(claim.error as Error).message}</p></div>

  const c = claim.data!.data
  return (
    <div className="page page-narrow stack">
      <div className="panel">
        <div className="panel-head">
          <span className="grow">{c.product_name || c.policy}</span>
          <span className={`state ${c.status}`}>{t(c.status)}</span>
        </div>
        <div className="panel-body stack">
          <div className="figures">
            <div className="figure">
              <span className="label">{t('claimed')}</span>
              <span className="value num">{money(c.amount)}</span>
            </div>
            <div className="figure">
              <span className="label">{t('paidWord')}</span>
              <span className="value num">{money(c.paid)}</span>
            </div>
            <div className="figure">
              <span className="label">{t('outstanding')}</span>
              <span className="value num">{money(c.outstanding)}</span>
            </div>
            <div className="figure">
              <span className="label">{t('holder')}</span>
              <span className="value">{c.holder}</span>
            </div>
            <div className="figure">
              <span className="label">{t('incurredOn')}</span>
              <span className="value">{day(c.incurred_at)}</span>
            </div>
            <div className="figure">
              <span className="label">{t('reportedOn')}</span>
              <span className="value">{day(c.reported_at)}</span>
            </div>
            {c.closed_at && (
              <div className="figure">
                <span className="label">{t('closedOn')}</span>
                <span className="value">{day(c.closed_at)}</span>
              </div>
            )}
            <div className="figure wide">
              <span className="label">{t('policyId')}</span>
              <span className="value mono">{c.policy}</span>
            </div>
          </div>
          <Evidence claim={c} filename={claim.data!.evidence[0]?.filename} />
        </div>
      </div>

      {canDecide && c.status === 'open' && <Decide claim={c} />}

      {claim.data!.payments.length > 0 && (
        <div className="panel">
          <div className="panel-head"><span className="grow">{t('payments')}</span></div>
          <div className="panel-body flush">
            <table className="table">
              <thead><tr><th>{t('time')}</th><th className="num">{t('amount')}</th><th>{t('txid')}</th></tr></thead>
              <tbody>
                {claim.data!.payments.map(p => (
                  <tr key={p.txid}>
                    <td className="nowrap">{day(p.paid_at)}</td>
                    <td className="num">{money(p.amount)}</td>
                    <td className="mono">{p.txid.slice(0, 12)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {claim.data!.decisions.length > 0 && (
        <div className="panel">
          <div className="panel-head"><span className="grow">{t('decisions')}</span></div>
          <div className="panel-body flush">
            {claim.data!.decisions.map((d, i) => (
              <div className="list-row" key={i}>
                <div className="grow">
                  <div className="title">{t(d.action)}{d.amount ? ` · ${money(d.amount)}` : ''}</div>
                  <div className="sub">{d.reason || '—'}</div>
                </div>
                <div className="sub nowrap">{d.decided_by} · {day(d.decided_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p><Link to="/claims">← {t('myClaims')}</Link></p>
    </div>
  )
}

/**
 * The evidence, fetched with the caller's token and handed to the browser as a
 * download — a plain link would arrive without the Authorization header.
 */
function Evidence({ claim, filename }: { claim: Claim; filename?: string }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [busy, setBusy] = useState(false)

  const download = async () => {
    setBusy(true)
    try {
      const res = await fetch(`${API_BASE}/claims/${encodeURIComponent(claim.id)}/evidence`, {
        headers: { Authorization: `Bearer ${authToken() ?? ''}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = filename || `${claim.id}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      message.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="row">
      <button className="btn-ghost" onClick={download} disabled={busy}>
        <DownloadOutlined /> {t('downloadEvidence')}
      </button>
      <span className="muted mono" style={{ fontSize: 11 }}>{claim.evidence_sha256.slice(0, 16)}…</span>
    </div>
  )
}

/** Pay part of a claim, close it, or refuse it with a reason. */
export function Decide({ claim }: { claim: Claim }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { me } = useSession()
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [pin, setPin] = useState('')

  const done = (text: string) => {
    message.success(text)
    setAmount(''); setReason(''); setPin('')
    qc.invalidateQueries({ queryKey: ['claim', claim.id] })
    qc.invalidateQueries({ queryKey: ['claims'] })
    qc.invalidateQueries({ queryKey: ['queue'] })
  }
  const fail = (e: Error) => message.error(e.message)

  const pay = useMutation({
    mutationFn: () => api.pay(claim.id, { amount: Number(amount), pin: pin || undefined, reason }),
    onSuccess: () => done(t('paidWord')), onError: fail,
  })
  const settle = useMutation({
    mutationFn: () => api.settle(claim.id, reason), onSuccess: () => done(t('settled')), onError: fail,
  })
  const reject = useMutation({
    mutationFn: () => api.reject(claim.id, reason), onSuccess: () => done(t('rejected')), onError: fail,
  })

  const owed = Number(claim.outstanding)
  const payable = Number(amount) > 0 && Number(amount) <= owed
  const busy = pay.isPending || settle.isPending || reject.isPending

  return (
    <div className="panel">
      <div className="panel-head"><span className="grow">{t('decisions')}</span></div>
      <div className="panel-body stack" style={{ gap: 12 }}>
        <div className="field">
          <label htmlFor="pay-amount">{t('amountToPay')}<b>{t('outstanding')} {money(owed)}</b></label>
          <div className="input-wrap">
            <input id="pay-amount" value={amount} inputMode="numeric" autoComplete="off" placeholder="0"
              onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="reason">{t('reason')}</label>
          <div className="input-wrap">
            <input id="reason" value={reason} onChange={e => setReason(e.target.value)} autoComplete="off" />
          </div>
        </div>
        {me?.pin?.set && (
          <div className="field">
            <label htmlFor="decide-pin">{t('pin')}</label>
            <div className="input-wrap">
              <input id="decide-pin" value={pin} type="password" inputMode="numeric" autoComplete="off"
                onChange={e => setPin(e.target.value)} />
            </div>
          </div>
        )}
        <p className="field-hint">{t('settleNote')}</p>
        <div className="review-actions">
          <button className="btn-ghost" disabled={busy || !reason.trim()} onClick={() => reject.mutate()}>
            {t('reject')}
          </button>
          <button className="btn-ghost" disabled={busy} onClick={() => settle.mutate()}>{t('settle')}</button>
        </div>
        <button className="submit buy" disabled={busy || !payable} onClick={() => pay.mutate()}>{t('pay')}</button>
      </div>
    </div>
  )
}
